import React, { useCallback, useState } from "react";
import PopoverButton from "@dtci/spellbook/PopoverButton";
import MenuList from "@dtci/spellbook/MenuList";
import { retryFailedEnrichment } from "ppt/api";
import { usePPTToasts } from "../../hooks/usePPTToasts";

const ACTION_RETRY_FAILED = "retry-failed";

const OPTIONS = [
  {
    value: ACTION_RETRY_FAILED,
    label: "Retry Failed Enrichments",
  },
];

/**
 * Kebab menu attached to the PPT landing-page header. Houses system-wide
 * ops recovery actions that don't belong on a specific promotion's detail
 * page.
 *
 * Today: a single item — "Retry Failed Enrichments" — that flips every
 * FAILED enrichment_queue row back to PENDING for the worker to re-process.
 * The natural recovery action after a Rightsline outage; the per-promotion
 * Re-sync can't help with entries that never produced a `client_discount`.
 *
 * When this menu grows past ~3 items, factor into a dedicated admin page
 * (see technical-debt #46: "no admin actions endpoints" and the user-led
 * future-proofing discussion).
 *
 * Open to anyone with `hasPptPermission` for now; the underlying endpoint
 * is also open (per the existing `EnrichmentQueueController` TODO(rbac)).
 */
export const PPTAdminActionsMenu = () => {
  const { showSuccessToast, showErrorToast } = usePPTToasts();
  const [busy, setBusy] = useState(false);

  const handleRetryFailed = useCallback(async () => {
    setBusy(true);
    try {
      const { retriedCount } = await retryFailedEnrichment();
      const message =
        retriedCount > 0
          ? `Retried ${retriedCount} failed enrichment${retriedCount === 1 ? "" : "s"} — the worker will process them shortly.`
          : "No failed enrichments to retry.";
      showSuccessToast(message);
    } catch (err) {
      console.error("Retry Failed Enrichments failed:", err);
      showErrorToast("Retry Failed Enrichments failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [showSuccessToast, showErrorToast]);

  const handleSelect = useCallback(
    (option) => {
      if (busy) return;
      switch (option?.value) {
        case ACTION_RETRY_FAILED:
          handleRetryFailed();
          break;
        default:
          break;
      }
    },
    [busy, handleRetryFailed],
  );

  return (
    <PopoverButton
      isDisabled={busy}
      buttonProps={{ styleType: "link", colorType: "base" }}
      testMetadata={{ button: { "data-testid": "ppt-admin-actions-kebab" } }}
    >
      <MenuList options={OPTIONS} onChange={handleSelect} isStandalone />
    </PopoverButton>
  );
};

export default PPTAdminActionsMenu;
